import React, { useState, useRef, useCallback, useEffect } from 'react';
import { RotateCcw } from 'lucide-react';

interface ResizableLayoutProps {
  children: React.ReactNode[];
  onLayoutChange?: (layout: LayoutConfig) => void;
  initialLayout?: LayoutConfig;
}

interface LayoutConfig {
  newMessageWidth: number;
  conversationsWidth: number;
  messageViewWidth: number;
  countriesHeight: number; // Add height control for countries section
}

const DEFAULT_LAYOUT: LayoutConfig = {
  newMessageWidth: 25, // 25% of screen width
  conversationsWidth: 25, // 25% of screen width
  messageViewWidth: 50, // 50% of screen width
  countriesHeight: 120, // Fixed height in pixels for countries section
};

const MIN_PANEL_WIDTH = 15; // Minimum 15% width
const MAX_PANEL_WIDTH = 60; // Maximum 60% width
const MIN_COUNTRIES_HEIGHT = 80; // Minimum height for countries section
const MAX_COUNTRIES_HEIGHT = 200; // Maximum height for countries section

export default function ResizableLayout({ 
  children, 
  onLayoutChange, 
  initialLayout = DEFAULT_LAYOUT 
}: ResizableLayoutProps) {
  const [layout, setLayout] = useState<LayoutConfig>(initialLayout);
  const [isDragging, setIsDragging] = useState<string | null>(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartLayout, setDragStartLayout] = useState<LayoutConfig>(layout);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load saved layout from localStorage on mount
  useEffect(() => {
    const savedLayout = localStorage.getItem('whatsapp-layout');
    if (savedLayout) {
      try {
        const parsedLayout = JSON.parse(savedLayout);
        setLayout(parsedLayout);
        onLayoutChange?.(parsedLayout);
      } catch (error) {
        console.error('Failed to parse saved layout:', error);
      }
    }
  }, [onLayoutChange]);

  // Save layout to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('whatsapp-layout', JSON.stringify(layout));
    onLayoutChange?.(layout);
  }, [layout, onLayoutChange]);

  const handleMouseDown = useCallback((divider: string, e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(divider);
    setDragStartX(e.clientX);
    setDragStartY(e.clientY);
    setDragStartLayout(layout);
    
    if (divider === 'countries') {
      document.body.style.cursor = 'row-resize';
    } else {
      document.body.style.cursor = 'col-resize';
    }
    document.body.style.userSelect = 'none';
  }, [layout]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    let newLayout = { ...dragStartLayout };

    if (isDragging === 'first') {
      // Resizing between New Message and Conversations
      const containerWidth = containerRect.width;
      const deltaX = e.clientX - dragStartX;
      const deltaPercent = (deltaX / containerWidth) * 100;

      const newNewMessageWidth = Math.max(
        MIN_PANEL_WIDTH,
        Math.min(MAX_PANEL_WIDTH, dragStartLayout.newMessageWidth + deltaPercent)
      );
      const newConversationsWidth = Math.max(
        MIN_PANEL_WIDTH,
        Math.min(MAX_PANEL_WIDTH, dragStartLayout.conversationsWidth - deltaPercent)
      );

      // Ensure total doesn't exceed 100%
      const totalOtherPanels = newLayout.messageViewWidth;
      const availableWidth = 100 - totalOtherPanels;
      const totalNewWidth = newNewMessageWidth + newConversationsWidth;

      if (totalNewWidth <= availableWidth) {
        newLayout.newMessageWidth = newNewMessageWidth;
        newLayout.conversationsWidth = newConversationsWidth;
      }
    } else if (isDragging === 'second') {
      // Resizing between Conversations and Message View
      const containerWidth = containerRect.width;
      const deltaX = e.clientX - dragStartX;
      const deltaPercent = (deltaX / containerWidth) * 100;

      const newConversationsWidth = Math.max(
        MIN_PANEL_WIDTH,
        Math.min(MAX_PANEL_WIDTH, dragStartLayout.conversationsWidth + deltaPercent)
      );
      const newMessageViewWidth = Math.max(
        MIN_PANEL_WIDTH,
        Math.min(MAX_PANEL_WIDTH, dragStartLayout.messageViewWidth - deltaPercent)
      );

      // Ensure total doesn't exceed 100%
      const totalOtherPanels = newLayout.newMessageWidth;
      const availableWidth = 100 - totalOtherPanels;
      const totalNewWidth = newConversationsWidth + newMessageViewWidth;

      if (totalNewWidth <= availableWidth) {
        newLayout.conversationsWidth = newConversationsWidth;
        newLayout.messageViewWidth = newMessageViewWidth;
      }
    } else if (isDragging === 'countries') {
      // Resizing countries section height
      const deltaY = e.clientY - dragStartY;
      const newCountriesHeight = Math.max(
        MIN_COUNTRIES_HEIGHT,
        Math.min(MAX_COUNTRIES_HEIGHT, dragStartLayout.countriesHeight + deltaY)
      );
      newLayout.countriesHeight = newCountriesHeight;
    }

    setLayout(newLayout);
  }, [isDragging, dragStartX, dragStartY, dragStartLayout]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(null);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const resetLayout = () => {
    setLayout(DEFAULT_LAYOUT);
    localStorage.removeItem('whatsapp-layout');
  };

  return (
    <div className="flex-1 flex min-h-0 relative" ref={containerRef}>
      {/* Reset Layout Button */}
      <button
        onClick={resetLayout}
        className="absolute top-4 right-4 z-10 flex items-center gap-2 px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 bg-white/80 hover:bg-white border border-gray-200 rounded-lg shadow-sm transition-all duration-200"
        title="Reset to default layout"
      >
        <RotateCcw className="w-3 h-3" />
        Reset Layout
      </button>

      {/* New Message Panel */}
      <div 
        className="bg-white border-r overflow-y-auto transition-all duration-200 ease-out"
        style={{ width: `${layout.newMessageWidth}%` }}
      >
        {children[0]}
      </div>

      {/* First Divider */}
      <div
        className={`w-1 bg-gray-200 hover:bg-blue-400 cursor-col-resize transition-all duration-200 relative group ${
          isDragging === 'first' ? 'bg-blue-500 shadow-lg' : ''
        }`}
        onMouseDown={(e) => handleMouseDown('first', e)}
      >
        <div className="absolute inset-y-0 -left-1 -right-1 group-hover:bg-blue-400/20 transition-colors duration-200" />
        {isDragging === 'first' && (
          <div className="absolute inset-y-0 -left-2 -right-2 bg-blue-500/10 animate-pulse" />
        )}
      </div>

      {/* Conversations Panel with Countries and Conversations List */}
      <div 
        className="bg-white border-r flex flex-col min-h-0 transition-all duration-200 ease-out"
        style={{ width: `${layout.conversationsWidth}%` }}
      >
        {/* Countries Section */}
        <div 
          className="flex-shrink-0 border-b bg-gray-50 overflow-hidden"
          style={{ height: `${layout.countriesHeight}px` }}
        >
          {children[1]}
        </div>

        {/* Countries Resize Handle */}
        <div
          className={`h-1 bg-gray-200 hover:bg-blue-400 cursor-row-resize transition-all duration-200 relative group ${
            isDragging === 'countries' ? 'bg-blue-500 shadow-lg' : ''
          }`}
          onMouseDown={(e) => handleMouseDown('countries', e)}
        >
          <div className="absolute inset-x-0 -top-1 -bottom-1 group-hover:bg-blue-400/20 transition-colors duration-200" />
          {isDragging === 'countries' && (
            <div className="absolute inset-x-0 -top-2 -bottom-2 bg-blue-500/10 animate-pulse" />
          )}
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {children[2]}
        </div>
      </div>

      {/* Second Divider */}
      <div
        className={`w-1 bg-gray-200 hover:bg-blue-400 cursor-col-resize transition-all duration-200 relative group ${
          isDragging === 'second' ? 'bg-blue-500 shadow-lg' : ''
        }`}
        onMouseDown={(e) => handleMouseDown('second', e)}
      >
        <div className="absolute inset-y-0 -left-1 -right-1 group-hover:bg-blue-400/20 transition-colors duration-200" />
        {isDragging === 'second' && (
          <div className="absolute inset-y-0 -left-2 -right-2 bg-blue-500/10 animate-pulse" />
        )}
      </div>

      {/* Message View Panel */}
      <div 
        className="bg-white flex flex-col min-h-0 transition-all duration-200 ease-out"
        style={{ width: `${layout.messageViewWidth}%` }}
      >
        {children[3]}
      </div>

      {/* Drag Overlay */}
      {isDragging && (
        <div className="absolute inset-0 bg-transparent z-50" style={{
          cursor: isDragging === 'countries' ? 'row-resize' : 'col-resize'
        }} />
      )}
    </div>
  );
}
